import { useEffect, useRef, useState } from 'react';
import { QueryClient, useMutation, useQuery, useQueryClient } from 'react-query';
import { notificationsAxiosApi } from 'src/Config/axiosApi';
import {
  CounterStuckPayload,
  NotificationEvent,
  OrphanedAutomationFillPayload,
  TRIAGE_MAX_IDS_PER_WRITE,
  TriageBucketPage,
  TriageListResponse,
  TriageNotification,
  TriageReadFrame,
  TriageReadResponse,
  TriageUnreadResponse,
  triageBucketOf,
} from '../types/notifications.types';
import {
  TRIAGE_INDEX_POLL_MS,
  TRIAGE_REFRESH_COALESCE_MS,
  useTriageSocketFrames,
} from './useTriageIndex';
import { TRIAGE_LIST_KEY_ROOT } from './triageKeys';

/**
 * The triage tray itself: `GET /notifications/triage` (spec §4.5) plus the two
 * write endpoints that clear and un-clear a card.
 *
 * Read state is SERVER-SIDE and GLOBAL — this hook never marks anything on its
 * own. The only writes it makes are the ones an operator asked for, with the
 * ids it is actually rendering.
 */

export type TriageStateFilter = 'unread' | 'read' | 'all';

/** The pair-scope half of a cache key — see {@link triageListKey}. */
export type TriagePairScopeKey = 'scoped' | 'retired';

export { TRIAGE_LIST_KEY_ROOT } from './triageKeys';

/** Server default; the endpoint caps at 200. */
export const TRIAGE_LIST_LIMIT = 100;

/**
 * What the tab badge asks for: it renders two integers and an age, so it has
 * no use for the page. Not `0` — the controller reads it as UNSET
 * (`parseInt(raw) || DEFAULT_LIMIT`) and serves the full default page, which is
 * the opposite of this.
 */
export const TRIAGE_BADGE_LIMIT = 1;

/**
 * The `limit` is part of the key: two callers on the same state with different
 * page sizes get their OWN entry, so the badge cannot be served the tray's
 * page (nor drag it down to its own).
 *
 * So is the pair scope, and for a sharper reason: the two scopes answer
 * different questions with the same shape. Sharing an entry would let the
 * scoped tray render the widened response's counts — 827 where 15 are open —
 * for one poll interval after the toggle flips back, which is the exact number
 * this change exists to stop showing.
 */
export function triageListKey(
  state: TriageStateFilter,
  limit: number = TRIAGE_LIST_LIMIT,
  includeRetired = false,
) {
  return [
    TRIAGE_LIST_KEY_ROOT,
    state,
    limit,
    includeRetired ? 'retired' : 'scoped',
  ] as const;
}

export const TRIAGE_LIST_POLL_MS = TRIAGE_INDEX_POLL_MS;

export const TRIAGE_LIST_STALE_AFTER_MS = TRIAGE_LIST_POLL_MS * 3;

/** The unread counts are capped server-side at 1000; past it we cannot count. */
export const TRIAGE_UNREAD_CAP_LABEL = '999+';

export class TriageListMalformedError extends Error {
  constructor() {
    super('triage response is missing a required bucket');
    this.name = 'TriageListMalformedError';
  }
}

export class TriageEmptyBatchError extends Error {
  constructor() {
    super('nothing to mark — the batch carried no ids');
    this.name = 'TriageEmptyBatchError';
  }
}

export class TriageBatchTooLargeError extends Error {
  constructor(size: number) {
    super(
      `refusing to post ${size} ids — the endpoint accepts at most ${TRIAGE_MAX_IDS_PER_WRITE}`,
    );
    this.name = 'TriageBatchTooLargeError';
  }
}

function isUsableBucket(value: unknown): value is TriageBucketPage {
  const v = value as Partial<TriageBucketPage> | null | undefined;
  return (
    !!v &&
    typeof v.unread === 'number' &&
    Array.isArray(v.items) &&
    // Validated, not defaulted. These say how many cards the bucket is NOT
    // showing; reading an absent one as `0` prints "nothing hidden" over
    // whatever the scope removed, which is the same lie `includesRetired`
    // exists to prevent one level up.
    typeof v.hidden === 'number' &&
    typeof v.hiddenUnattributed === 'number' &&
    typeof v.hiddenCapped === 'boolean'
  );
}

/**
 * A missing bucket is not an empty bucket. Rendering `0 unread` over a payload
 * that never said so is the one failure this tray exists to prevent, so a
 * malformed response is rejected and the tray says *unreadable*.
 *
 * `oldestUnreadAt` is required for the same reason one step further in: the
 * header reads an absent field as `null` and prints *Nothing unread* over
 * whatever the buckets are carrying.
 */
export function isUsableTriageList(value: unknown): value is TriageListResponse {
  const v = value as Partial<TriageListResponse> | null | undefined;
  return (
    !!v &&
    isUsableBucket(v.counter) &&
    isUsableBucket(v.orphan) &&
    (typeof v.oldestUnreadAt === 'string' || v.oldestUnreadAt === null) &&
    // 🚨 REQUIRED, not optional-with-a-default. It is the label on every other
    // number in the response — whether these counts are the actionable subset
    // or everything — and a screen that renders them without knowing which is
    // the `827 where 15 are open` this scope exists to stop. A backend that
    // predates it cannot answer, so the tray says UNREADABLE rather than
    // guessing, which is also what the deploy order requires (core first).
    typeof v.includesRetired === 'boolean' &&
    // Same class of label: it says these numbers are UNSCOPED. Absent would
    // read as `false` — scoped — and the banner would never fire.
    typeof v.pairScopeCapped === 'boolean'
  );
}

export async function fetchTriageList(params: {
  state: TriageStateFilter;
  limit?: number;
  includeRetired?: boolean;
}): Promise<TriageListResponse> {
  const { data } = await notificationsAxiosApi.get<TriageListResponse>(
    '/notifications/triage',
    {
      params: {
        state: params.state,
        limit: params.limit ?? TRIAGE_LIST_LIMIT,
        // Sent explicitly in BOTH directions. The server defaults to the
        // scoped view, but a client that only ever sends the widening value
        // and omits the other cannot be told apart from an old build, and the
        // difference between those two is whether a settled market's card is
        // on the operator's screen.
        retired: params.includeRetired ? 'include' : 'exclude',
      },
    },
  );
  if (!isUsableTriageList(data)) throw new TriageListMalformedError();
  return data;
}

/* ── Cache application (the frames carry the facts — except where an id
      names a card no entry holds, which only the server can price) ───────── */

function stateOfKey(key: unknown): TriageStateFilter | null {
  if (!Array.isArray(key) || key[0] !== TRIAGE_LIST_KEY_ROOT) return null;
  const state = key[1];
  return state === 'unread' || state === 'read' || state === 'all'
    ? state
    : null;
}

/**
 * Which pair scope a cache entry answers for.
 *
 * 🚨 Every cross-entry DELTA must be scoped by this. The two entries answer
 * different questions over overlapping card sets: the scoped one counts only
 * cards on live pairs, the widened one counts all of them. A delta measured
 * across BOTH and applied to each subtracts, from the scoped entry's count,
 * cards that entry never counted — and the direction is the dangerous one.
 * Clearing 5 hidden cards from the widened view over a scoped `unread: 1`
 * drives it to 0, `oldestUnreadAt` to `null`, and the header prints *Nothing
 * unread* with the open row still on screen.
 *
 * ONE writer is deliberately NOT scoped: `applyTriageEventToCache`, which
 * folds a live card in. A notification carries no pair lifecycle, so the
 * client cannot tell whether the scoped entry would have counted it — and the
 * two answers are not symmetric. Inserting it shows a card the next poll may
 * drop (over-shows, safe, self-correcting in one interval); withholding it
 * hides a naked leg for that same interval. It inserts.
 *
 * An entry whose key predates the scope element is the scoped one, which is
 * what it was.
 */
function scopeOfKey(key: unknown): TriagePairScopeKey | null {
  if (!Array.isArray(key) || key[0] !== TRIAGE_LIST_KEY_ROOT) return null;
  const scope = key[3];
  if (scope === undefined) return 'scoped';
  return scope === 'scoped' || scope === 'retired' ? scope : null;
}

function bucketAdmits(state: TriageStateFilter, read: boolean): boolean {
  if (state === 'all') return true;
  return state === 'read' ? read : !read;
}

/**
 * Stamp `readAt`/`readBy` on the ids we hold. Idempotent by construction: an
 * item already read is left alone and does NOT decrement the count again, which
 * is what lets our own write and the socket frame echoing it both apply.
 *
 * In an unread-filtered cache entry a cleared card is dropped rather than
 * greyed — the entry is the answer to "what is unread", and a read row sitting
 * in it would be that answer lying.
 *
 * 🚨 `unreadDelta` is decided ACROSS the cache (`heldByBucket`), not by
 * what THIS entry holds: `limit` is per bucket, so the badge's one-card page
 * holds almost none of the ids a `Mark all shown read` posts. Counting only the
 * ids in hand put a badge reading 39 over a tray reading `Nothing here.`
 */
function applyReadToBucket<T>(
  bucket: TriageBucketPage<T>,
  ids: ReadonlySet<string>,
  readAt: string | null,
  readBy: string | null,
  state: TriageStateFilter,
  unreadDelta: number,
): TriageBucketPage<T> {
  const items: Array<TriageNotification<T>> = [];
  for (const item of bucket.items) {
    if (!ids.has(item.id)) {
      items.push(item);
      continue;
    }
    if (item.readAt) {
      // Already cleared: leave `readAt`/`readBy` exactly as they are. Re-
      // stamping would overwrite WHO cleared it — and the auto-clear's
      // `readBy` is the only thing that makes an auto-cleared card say so.
      if (bucketAdmits(state, true)) items.push(item);
      continue;
    }
    if (!bucketAdmits(state, true)) continue;
    items.push({ ...item, readAt: readAt ?? new Date().toISOString(), readBy });
  }
  if (unreadDelta === 0 && items.length === bucket.items.length) return bucket;
  return {
    ...bucket,
    items,
    unread: Math.max(0, bucket.unread - unreadDelta),
  };
}

function applyUnreadToBucket<T>(
  bucket: TriageBucketPage<T>,
  ids: ReadonlySet<string>,
  state: TriageStateFilter,
  unreadDelta: number,
): TriageBucketPage<T> {
  const items: Array<TriageNotification<T>> = [];
  for (const item of bucket.items) {
    if (!ids.has(item.id)) {
      items.push(item);
      continue;
    }
    if (!item.readAt) {
      if (bucketAdmits(state, false)) items.push(item);
      continue;
    }
    if (!bucketAdmits(state, false)) continue;
    items.push({ ...item, readAt: null, readBy: null });
  }
  if (unreadDelta === 0 && items.length === bucket.items.length) return bucket;
  return { ...bucket, items, unread: bucket.unread + unreadDelta };
}

/**
 * How many of `ids` the CACHE — every entry, not one page — is holding in a
 * given read state, per bucket. That count is the delta every entry then
 * applies, so entries on different `limit`s move together.
 *
 * Still idempotent: applying the frame leaves no entry holding those ids in the
 * old state, so a re-delivered frame counts zero.
 *
 * 🚨 An id NO entry holds counts zero, and the two directions land on OPPOSITE
 * sides of that:
 *  · READ — the badge keeps counting a card that was cleared, i.e. it
 *    over-states until the poll. Safe: never a clean badge over an open leg.
 *  · UNREAD — the badge does NOT come back up over a card that was reopened,
 *    which is exactly that clean badge. And it is the ordinary case: the badge
 *    entry is `state:'unread'`, so by definition it holds no read items to
 *    count. `applyUnreadToCache` therefore schedules a refetch whenever the
 *    frame names a card no entry holds.
 */
function heldByBucket(
  qc: QueryClient,
  ids: ReadonlySet<string>,
  wantRead: boolean,
  scope: TriagePairScopeKey,
): { counter: number; orphan: number } {
  const held = { counter: new Set<string>(), orphan: new Set<string>() };
  for (const [key, cached] of qc.getQueriesData<TriageListResponse>(
    TRIAGE_LIST_KEY_ROOT,
  )) {
    if (!stateOfKey(key) || scopeOfKey(key) !== scope || !cached) continue;
    for (const bucket of ['counter', 'orphan'] as const) {
      const page = cached[bucket] as unknown as TriageBucketPage;
      for (const item of page.items) {
        if (ids.has(item.id) && !!item.readAt === wantRead) {
          held[bucket].add(item.id);
        }
      }
    }
  }
  return { counter: held.counter.size, orphan: held.orphan.size };
}

/* ── `oldestUnreadAt` ──────────────────────────────────────────────────── */

/** Earlier of two stamps; an unparseable one never wins. */
function minStamp(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta)) return b;
  if (Number.isNaN(tb)) return a;
  return ta <= tb ? a : b;
}

function unreadItemsOf(
  list: TriageListResponse,
): Array<{ readAt?: string | null; occurredAt: string }> {
  return [...list.counter.items, ...list.orphan.items].filter(
    (item) => !item.readAt,
  );
}

/**
 * The oldest unread stamp after cards were cleared.
 *
 * Clearing can only move it forward or to `null`. Past that it can only be
 * ASSERTED while the entry holds every unread card it counts — a page is a
 * window, and the true oldest may be older than anything in hand. When it
 * cannot be, the last server answer stands (older, never emptier) until the
 * poll replaces it.
 *
 * 🚨 The `totalUnread === 0` branch is the ONE place this file asserts
 * something the server did not say — and what it asserts is *Nothing unread.*
 * The total is local arithmetic: a server count that came in LOW is driven to
 * zero by clearing what is on screen, and the header then prints that line over
 * cards nobody cleared. It stands because the counts and the stamp have to
 * agree — a `0 unread` under an "oldest unread" age is the same contradiction
 * read the other way — and because the poll replaces both within one interval.
 */
function oldestUnreadAfterClear(
  prev: string | null,
  next: TriageListResponse,
): string | null {
  const totalUnread = next.counter.unread + next.orphan.unread;
  if (totalUnread === 0) return null;
  const held = unreadItemsOf(next);
  if (held.length !== totalUnread) return prev;
  return held.reduce<string | null>(
    (oldest, item) => minStamp(oldest, item.occurredAt),
    null,
  );
}

export function applyReadFrameToCache(
  qc: QueryClient,
  frame: TriageReadFrame,
): void {
  const ids = new Set(frame.ids);
  if (ids.size === 0) return;
  // One delta per pair scope, memoised — see `scopeOfKey` for why it may not
  // be one delta across all of them.
  const clearedByScope = new Map<
    TriagePairScopeKey,
    { counter: number; orphan: number }
  >();
  for (const [key, cached] of qc.getQueriesData<TriageListResponse>(
    TRIAGE_LIST_KEY_ROOT,
  )) {
    const state = stateOfKey(key);
    const scope = scopeOfKey(key);
    if (!state || !scope || !cached) continue;
    let cleared = clearedByScope.get(scope);
    if (!cleared) {
      cleared = heldByBucket(qc, ids, false, scope);
      clearedByScope.set(scope, cleared);
    }
    const next: TriageListResponse = {
      ...cached,
      counter: applyReadToBucket(
        cached.counter,
        ids,
        frame.readAt,
        frame.readBy,
        state,
        cleared.counter,
      ),
      orphan: applyReadToBucket(
        cached.orphan,
        ids,
        frame.readAt,
        frame.readBy,
        state,
        cleared.orphan,
      ),
    };
    qc.setQueryData<TriageListResponse>(key, {
      ...next,
      oldestUnreadAt: oldestUnreadAfterClear(cached.oldestUnreadAt, next),
    });
  }
}

/**
 * How many of `ids` no cache entry OF THIS SCOPE holds, in either read state.
 *
 * Scoped for the same reason the delta is: an undo on a card only the widened
 * entry holds leaves the scoped entry's count untouched and un-refreshed —
 * a badge staying clean over a card that is open again.
 */
function idsHeldNowhere(
  qc: QueryClient,
  ids: ReadonlySet<string>,
  scope: TriagePairScopeKey,
): number {
  const held = new Set<string>();
  for (const [key, cached] of qc.getQueriesData<TriageListResponse>(
    TRIAGE_LIST_KEY_ROOT,
  )) {
    if (!stateOfKey(key) || scopeOfKey(key) !== scope || !cached) continue;
    for (const bucket of ['counter', 'orphan'] as const) {
      const page = cached[bucket] as unknown as TriageBucketPage;
      for (const item of page.items) {
        if (ids.has(item.id)) held.add(item.id);
      }
    }
  }
  return ids.size - held.size;
}

/**
 * A pending coalesced refetch of the list entries, per client — the same shape
 * `useTriageIndex` uses for its own frames: one burst of ids, one read.
 */
const pendingListRefresh = new WeakMap<
  QueryClient,
  ReturnType<typeof setTimeout>
>();

/** Ask the server for the counts this cache cannot derive from the frame. */
function scheduleListRefresh(qc: QueryClient): void {
  if (pendingListRefresh.has(qc)) return;
  pendingListRefresh.set(
    qc,
    setTimeout(() => {
      pendingListRefresh.delete(qc);
      void qc.invalidateQueries(TRIAGE_LIST_KEY_ROOT);
    }, TRIAGE_REFRESH_COALESCE_MS),
  );
}

export function applyUnreadToCache(qc: QueryClient, ids: string[]): void {
  const idSet = new Set(ids);
  if (idSet.size === 0) return;
  const restoredByScope = new Map<
    TriagePairScopeKey,
    { counter: number; orphan: number }
  >();
  for (const [key, cached] of qc.getQueriesData<TriageListResponse>(
    TRIAGE_LIST_KEY_ROOT,
  )) {
    const state = stateOfKey(key);
    const scope = scopeOfKey(key);
    if (!state || !scope || !cached) continue;
    let restoredCount = restoredByScope.get(scope);
    if (!restoredCount) {
      restoredCount = heldByBucket(qc, idSet, true, scope);
      restoredByScope.set(scope, restoredCount);
    }
    // 🚨 A card NO entry of THIS scope holds restores NOTHING, and on this
    // direction that is a badge staying clean over a card that is open again.
    // It is the ordinary case, not a corner: the badge's entry is
    // `state:'unread'` and by definition holds no read items, so a second
    // operator's undo moves it by zero. The frame carries ids, not counts, so
    // the server owns the answer.
    // `scheduleListRefresh` coalesces per client and invalidates the whole
    // key root, so ONE scope missing an id refetches both. No per-scope
    // bookkeeping here: it would encode a precision the refresh does not have.
    if (idsHeldNowhere(qc, idSet, scope) > 0) scheduleListRefresh(qc);
    // Restoring is exact wherever clearing is not: the cards coming back are
    // in hand, so the oldest of them against the current stamp IS the answer,
    // whatever the page does not hold.
    const restored = [...cached.counter.items, ...cached.orphan.items]
      .filter((item) => idSet.has(item.id) && !!item.readAt)
      .reduce<string | null>(
        (oldest, item) => minStamp(oldest, item.occurredAt),
        null,
      );
    qc.setQueryData<TriageListResponse>(key, {
      ...cached,
      counter: applyUnreadToBucket(
        cached.counter,
        idSet,
        state,
        restoredCount.counter,
      ),
      orphan: applyUnreadToBucket(
        cached.orphan,
        idSet,
        state,
        restoredCount.orphan,
      ),
      oldestUnreadAt: minStamp(cached.oldestUnreadAt, restored),
    });
  }
}

/** Live event ids already folded into this client's entries. */
const appliedLiveIds = new WeakMap<QueryClient, Set<string>>();

/** How many applied live ids we remember to recognise a re-delivery. */
const LIVE_EVENT_MEMORY = 5_000;

/**
 * Whether this delivery of `id` is the first one.
 *
 * 🚨 The per-page `items` check below cannot answer this on its own: it only
 * recognises an event the entry ADMITTED, and a `state:'read'` entry never
 * admits an unread card — so every re-delivery of the same live event would
 * bump that entry's `unread` again, over a page that can never hold the row.
 * The relay re-sends (several replicas, no consumer group), so that is not
 * hypothetical.
 */
function isFirstDeliveryOf(qc: QueryClient, id: string): boolean {
  let seen = appliedLiveIds.get(qc);
  if (!seen) {
    seen = new Set<string>();
    appliedLiveIds.set(qc, seen);
  }
  if (seen.has(id)) return false;
  if (seen.size >= LIVE_EVENT_MEMORY) seen.clear();
  seen.add(id);
  return true;
}

/**
 * A live triage event goes straight to the top of its tray and bumps the
 * badge. Nothing else moves — no refetch, no re-sort of the rows already on
 * screen — so the scroll position the operator is reading stays put.
 *
 * 🚨 The ONE writer here that does not respect `scopeOfKey`, and deliberately.
 * A notification carries no pair lifecycle, so the client cannot tell whether
 * the scoped entry would have counted this card, and the two ways of being
 * wrong are not symmetric: inserting shows a row the next poll may drop
 * (over-shows, self-correcting within one interval, and `hidden` goes up to
 * account for it), while withholding hides a naked leg for that same interval.
 * It inserts, into both entries, and the count moves with the row so the two
 * never disagree with each other.
 */
export function applyTriageEventToCache(
  qc: QueryClient,
  evt: NotificationEvent,
): void {
  const bucket = triageBucketOf(evt.type);
  if (!bucket) return;
  if (!isFirstDeliveryOf(qc, evt.id)) return;
  for (const [key, cached] of qc.getQueriesData<TriageListResponse>(
    TRIAGE_LIST_KEY_ROOT,
  )) {
    const state = stateOfKey(key);
    if (!state || !cached) continue;
    const page = cached[bucket] as unknown as TriageBucketPage;
    if (page.items.some((item) => item.id === evt.id)) continue;
    const next: TriageBucketPage = {
      ...page,
      unread: page.unread + 1,
      items: bucketAdmits(state, false)
        ? [{ ...evt, readAt: null, readBy: null }, ...page.items]
        : page.items,
    };
    // The card is unread and its stamp is in hand, so the header moves with
    // the tray: a live card landing in an empty tray must not leave *Nothing
    // unread* standing above it.
    const oldestUnreadAt = minStamp(cached.oldestUnreadAt, evt.occurredAt);
    qc.setQueryData<TriageListResponse>(
      key,
      bucket === 'counter'
        ? {
            ...cached,
            oldestUnreadAt,
            counter: next as unknown as TriageBucketPage<CounterStuckPayload>,
          }
        : {
            ...cached,
            oldestUnreadAt,
            orphan:
              next as unknown as TriageBucketPage<OrphanedAutomationFillPayload>,
          },
    );
  }
}

/* ── Hooks ────────────────────────────────────────────────────────────── */

export interface UseTriageListResult {
  /** `undefined` ⇒ the tray NEVER loaded ⇒ it renders "unreadable", not empty. */
  data: TriageListResponse | undefined;
  isStale: boolean;
  lastSuccessAt: number | null;
  isFetching: boolean;
  refetch: () => void;
}

/**
 * The `limit` travels with the cache key (see `triageListKey`), so a caller
 * that only needs the counts asks for a page it will not read instead of
 * sharing — and silently resizing — the tray's entry.
 */
export function useTriageList(opts?: {
  state?: TriageStateFilter;
  limit?: number;
  enabled?: boolean;
  /** Widen past the pair-lifecycle scope — see {@link triageListKey}. */
  includeRetired?: boolean;
}): UseTriageListResult {
  const state = opts?.state ?? 'unread';
  const limit = opts?.limit ?? TRIAGE_LIST_LIMIT;
  const includeRetired = opts?.includeRetired === true;
  const enabled = opts?.enabled !== false;
  const qc = useQueryClient();

  const query = useQuery<TriageListResponse>(
    triageListKey(state, limit, includeRetired),
    () => fetchTriageList({ state, limit, includeRetired }),
    {
      enabled,
      refetchInterval: TRIAGE_LIST_POLL_MS,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      retry: false,
    },
  );

  // Both frame kinds carry everything needed to update the rows in hand, so
  // they are applied straight into the cache. The poll behind them is what
  // reconciles a dropped frame, a second operator, or a pod restart — a
  // missing socket costs one interval, never a stale tray.
  useTriageSocketFrames(
    {
      // `readAt: null` is how the wire says UNREAD: the undo endpoint clears
      // the stamp and emits the same frame. Reading it as a clear would put
      // the card the operator just recovered straight back into the read view.
      onRead: (frame) =>
        frame.readAt === null
          ? applyUnreadToCache(qc, frame.ids)
          : applyReadFrameToCache(qc, frame),
      onTriageEvent: (evt) => applyTriageEventToCache(qc, evt),
    },
    { enabled },
  );

  const lastSuccessAt = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : null;
  // Gated on `enabled` alongside the fetch: a disabled query keeps the cached
  // entry's `dataUpdatedAt`, and nothing reads the age of a closed tray.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!enabled || lastSuccessAt === null) return;
    const timer = setInterval(() => tick((n) => n + 1), TRIAGE_LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, lastSuccessAt]);

  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;

  return {
    data: query.data,
    isStale:
      lastSuccessAt !== null &&
      Date.now() - lastSuccessAt > TRIAGE_LIST_STALE_AFTER_MS,
    lastSuccessAt,
    isFetching: query.isFetching,
    refetch: () => {
      void refetchRef.current();
    },
  };
}

function assertPostableBatch(ids: string[]): void {
  if (ids.length === 0) throw new TriageEmptyBatchError();
  if (ids.length > TRIAGE_MAX_IDS_PER_WRITE) {
    throw new TriageBatchTooLargeError(ids.length);
  }
}

/**
 * Clear cards by EXPLICIT id. There is deliberately no filter-based bulk
 * clear anywhere on this path: the tray posts the ids it is rendering, so
 * nothing the operator never saw can be silenced.
 */
export function useMarkTriageRead() {
  const qc = useQueryClient();
  return useMutation<TriageReadResponse, unknown, string[]>(
    async (ids) => {
      assertPostableBatch(ids);
      const { data } = await notificationsAxiosApi.post<TriageReadResponse>(
        '/notifications/read',
        { ids },
      );
      return data;
    },
    {
      mutationKey: 'triage:mark-read',
      onSuccess: (_res, ids) => {
        applyReadFrameToCache(qc, {
          ids,
          readAt: new Date().toISOString(),
          readBy: null,
        });
      },
    },
  );
}

/**
 * The undo. Not a nicety: a mis-click that silences a naked-leg card has to be
 * recoverable, and an operator who cannot undo learns not to click at all.
 */
export function useMarkTriageUnread() {
  const qc = useQueryClient();
  return useMutation<TriageUnreadResponse, unknown, string[]>(
    async (ids) => {
      assertPostableBatch(ids);
      const { data } = await notificationsAxiosApi.post<TriageUnreadResponse>(
        '/notifications/unread',
        { ids },
      );
      return data;
    },
    {
      mutationKey: 'triage:mark-unread',
      onSuccess: (_res, ids) => applyUnreadToCache(qc, ids),
    },
  );
}

export interface TriageUnreadTotals {
  /** `undefined` ⇒ never loaded. The badge renders nothing rather than `0`. */
  data: TriageListResponse | undefined;
  total: number;
  counter: number;
  orphan: number;
  /** A bucket hit the server-side count cap — the total is a floor, not a count. */
  capped: boolean;
  oldestUnreadAt: string | null;
  isStale: boolean;
}

/**
 * The unread totals behind the tab badge. It mounts on every tab, so it asks
 * for `TRIAGE_BADGE_LIMIT` — the counts and the oldest-unread stamp, not the
 * page.
 *
 * Its entry moves on the same frames as the tray's, and by the SAME delta
 * (`heldByBucket`) rather than by the one card its page happens to hold — the
 * two keys hold different pages of one tray, and a badge that decremented by
 * its own page would sit at 39 over a tray reading `Nothing here.` A card no
 * entry holds at all is not counted by either, and the poll settles it.
 */
export function useTriageUnreadTotals(opts?: {
  enabled?: boolean;
}): TriageUnreadTotals {
  const { data, isStale } = useTriageList({
    state: 'unread',
    limit: TRIAGE_BADGE_LIMIT,
    enabled: opts?.enabled,
  });
  const counter = data?.counter.unread ?? 0;
  const orphan = data?.orphan.unread ?? 0;
  return {
    data,
    counter,
    orphan,
    total: counter + orphan,
    capped: !!data?.counter.capped || !!data?.orphan.capped,
    oldestUnreadAt: data?.oldestUnreadAt ?? null,
    isStale,
  };
}

/** `999+` past the server-side count cap — never a number that stopped being true. */
export function formatUnreadBadge(total: number, capped: boolean): string {
  return capped ? TRIAGE_UNREAD_CAP_LABEL : String(total);
}
